/**
 * Shared grid column configuration for the Companies, People and Deals grids.
 *
 * Single source of truth for each grid's standard column set, localStorage
 * table-prefs key, and the UI-key → gridSummary fill-rate key mapping. Both the
 * pages (CompaniesPage/PeoplePage/DealsPage) and the idle prefetch
 * (use-idle-prefetch.ts) consume these so the prefetched getAccounts/
 * getContactsGridSummaries variables match the pages' own requests
 * byte-for-byte — a mismatched summaries cache entry is pure waste.
 *
 * Columns hidden out of the box carry `hiddenByDefault: true` on the ColumnDef
 * itself (there used to be a parallel `*_DEFAULT_HIDDEN_COLUMNS` array per grid,
 * which could drift from the column list). The flag also applies to snapshots
 * saved before the column existed — see `ColumnDef.hiddenByDefault`.
 *
 * Settings → Data Model also derives its standard-field list from these arrays
 * (see `@/lib/data-model/standard-fields`), so a column added here shows up there
 * too. Adding a column means adding its field type to that module.
 *
 * `label` is the ENGLISH DEFAULT, not what the grid header renders. Display goes
 * through `@/lib/grid/column-label`, which resolves `fields.<entity>.<key>` from
 * the locale catalogs and falls back to this label. The label stays here because
 * it is also the non-display vocabulary: the Data Model list, the CSV import
 * wizard and CSV export headers. Adding a column therefore means adding its key
 * to `locales/<lng>/fields.json` for every language — `column-label.test.ts`
 * fails otherwise.
 */

import type { ColumnDef } from "@/hooks/useTableColumns";
import { customColumnFillRateKey } from "@/lib/utils/custom-column";

// ---------------------------------------------------------------------------
// Companies grid
// ---------------------------------------------------------------------------

export const COMPANY_TABLE_STORAGE_KEY = "companies_v3";

export const COMPANY_GRID_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Company", sticky: true },
  { key: "website", label: "Website" },
  { key: "phone", label: "Phone" },
  { key: "location", label: "Location" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "industry", label: "Industry" },
  { key: "employees", label: "Employees" },
  { key: "revenue", label: "Annual Revenue" },
  { key: "sizeCategory", label: "Size Category", hiddenByDefault: true },
  { key: "description", label: "Description" },

  { key: "createdBy", label: "Created By" },
  { key: "createdDate", label: "Create Date", hiddenByDefault: true },
  { key: "foundingYear", label: "Founding Year", hiddenByDefault: true },
  { key: "street", label: "Street", hiddenByDefault: true },
  { key: "city", label: "City", hiddenByDefault: true },
  { key: "stateProvince", label: "State / Province", hiddenByDefault: true },
  { key: "country", label: "Country", hiddenByDefault: true },
  { key: "continent", label: "Continent", hiddenByDefault: true },
  { key: "postalCode", label: "Zip / Postal Code", hiddenByDefault: true },
  { key: "companyType", label: "Type", hiddenByDefault: true },
  { key: "funding", label: "Funding", hiddenByDefault: true },
  { key: "technologies", label: "Technologies", hiddenByDefault: true },
  { key: "lastEmail", label: "Last Email" },
  { key: "lastMeeting", label: "Last Meeting" },
  { key: "nextMeeting", label: "Next Meeting" },
  { key: "lastStep", label: "Last Step", hiddenByDefault: true },
  { key: "nextStep", label: "Next Step", hiddenByDefault: true },
  { key: "associations", label: "Associations", hiddenByDefault: true },
  { key: "source", label: "Source", hiddenByDefault: true },
];

const COMPANY_FILLRATE_KEY_MAP: Record<string, string> = {
  name: "properties.name",
  website: "domain",
  city: "properties.city",
  stateProvince: "properties.state",
  country: "properties.country",
  location: "location",
  industry: "properties.industry",
  // gridSummary returns fill rates keyed by the raw OpenSearch (snake_case) property
  // name; the GraphQL object camelCases them, but this map must match the OS keys or
  // the footer silently reads 0% on a populated column (grid_summary exact-matches).
  // `properties.employee_count` is a *composite* agg on the backend, covering
  // employee_count_min/max plus the range label — i.e. the whole family that backs the
  // employeeCountMin/Max the cell renders. Don't re-point this at a single property:
  // the ingest paths disagree about which ones they write (the direct list-build path
  // writes min/max and no label, MDM writes both), so any one field reads 0% for some
  // cohort. See coffee's grid_summary.py `_any_property_filled_agg`.
  employees: "properties.employee_count",
  revenue: "revenue",
  foundingYear: "properties.founded_year",
  phone: "properties.phone",
  linkedin: "properties.linkedin_url",
  lastEmail: "has_last_email",
  lastMeeting: "has_last_engagement",
  nextMeeting: "has_next_engagement",
  // "Created By" renders account.owner, which the backend resolves from the
  // owner_id property (api/account.py resolve_owner). There is no separate
  // created_by field — owner_id is the only "who put this here" property.
  createdBy: "properties.owner_id",
};

/** Map a Companies-grid UI column key to its gridSummary fill-rate key (null = no server agg). */
export const companyFillRateKeyFor = (uiKey: string): string | null => {
  if (COMPANY_FILLRATE_KEY_MAP[uiKey]) return COMPANY_FILLRATE_KEY_MAP[uiKey];
  if (uiKey.startsWith("custom_")) return customColumnFillRateKey(uiKey);
  return null;
};

// ---------------------------------------------------------------------------
// People grid
// ---------------------------------------------------------------------------

export const PEOPLE_TABLE_STORAGE_KEY = "people_v3";

export const PEOPLE_GRID_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Full Name", sticky: true },
  { key: "firstName", label: "First Name", hiddenByDefault: true },
  { key: "lastName", label: "Last Name", hiddenByDefault: true },
  { key: "title", label: "Title" },
  { key: "company", label: "Company" },
  { key: "email", label: "Email" },
  { key: "createdBy", label: "Created By" },
  { key: "createdDate", label: "Create Date", hiddenByDefault: true },
  { key: "phone", label: "Phone" },
  { key: "location", label: "Location" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "lastEmail", label: "Last Email" },
  { key: "lastMeeting", label: "Last Meeting" },
  { key: "nextMeeting", label: "Next Meeting" },
  { key: "lastStep", label: "Last Step" },
  { key: "nextStep", label: "Next Step" },
  { key: "source", label: "Source", hiddenByDefault: true },
];

// Map UI column keys to backend gridSummary `fillRates.columnName` keys.
// Backend emits OpenSearch-shaped names (e.g. `properties.first_name`, `primary_email`,
// `has_last_email`). Composites pick a single representative agg. Anything not listed
// (or not a `custom_*` prop) falls back to client-computed fill rate at the footer.
const PEOPLE_FILLRATE_KEY_MAP: Record<string, string> = {
  name: "properties.first_name",
  firstName: "properties.first_name",
  lastName: "properties.last_name",
  title: "properties.job_title",
  company: "has_account",
  email: "primary_email",
  phone: "properties.phone",
  location: "location",
  linkedin: "properties.linkedin_profile_url",
  lastEmail: "has_last_email",
  lastMeeting: "has_last_engagement",
  nextMeeting: "has_next_engagement",
  // "Created By" renders contact.owner, which the backend resolves from the
  // owner_id property (api/contact.py resolve_owner). There is no separate
  // created_by field — owner_id is the only "who put this here" property.
  createdBy: "properties.owner_id",
};

/** Map a People-grid UI column key to its gridSummary fill-rate key (null = no server agg). */
export const peopleFillRateKeyFor = (uiKey: string): string | null => {
  if (PEOPLE_FILLRATE_KEY_MAP[uiKey]) return PEOPLE_FILLRATE_KEY_MAP[uiKey];
  if (uiKey.startsWith("custom_")) return customColumnFillRateKey(uiKey);
  return null;
};

// ---------------------------------------------------------------------------
// Deals grid
//
// Lives here (rather than in DealsPage) so Settings → Data Model can derive the
// Deals standard-field list from the same array the grid renders. The two add-on
// columns stay separate because DealsPage splices them in after the standard set
// and before custom fields, and they're excluded from the CSV import wizard.
// ---------------------------------------------------------------------------

export const DEAL_GRID_COLUMNS: ColumnDef[] = [
  { key: "name", label: "Deal Name", sticky: true },
  { key: "company", label: "Company" },
  // Already fetched by DealPropertiesFields and writable via DealPropertiesInput,
  // but it had no column for a long stretch, so the data rode along unused. Hidden
  // by default: free text is rarely worth a grid column's width up front, and
  // existing users shouldn't have a new column appear in their saved layout.
  { key: "description", label: "Description", hiddenByDefault: true },
  // Deal Score is fabricated (hash-based dummy heuristics) — gated behind the
  // DEAL_SCORE flag so real production deals never show it.
  { key: "dealScore", label: "Deal Score" },
  { key: "stage", label: "Stage" },
  { key: "amount", label: "Amount" },
  { key: "closeDate", label: "Close Date" },
  { key: "owner", label: "Owner" },
  { key: "createdDate", label: "Create Date", hiddenByDefault: true },
  { key: "contacts", label: "Contacts" },
  { key: "lastEmail", label: "Last Email" },
  { key: "lastMeeting", label: "Last Meeting" },
  { key: "nextMeeting", label: "Next Meeting" },
  { key: "lastStep", label: "Last Step" },
  { key: "nextStep", label: "Next Step" },
  { key: "age", label: "Age", hiddenByDefault: true },
  { key: "timeInStage", label: "Time in Stage", hiddenByDefault: true },
  { key: "forecastCategory", label: "Forecast Category", hiddenByDefault: true },
];

export const DEAL_BILLING_COLUMN: ColumnDef = { key: "billing", label: "Billing", hiddenByDefault: true };
export const DEAL_PRODUCTS_COLUMN: ColumnDef = { key: "products", label: "Products", hiddenByDefault: true };
